import { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { css } from '@emotion/react';
import { color, Button } from '@uniquegood/realworld-studio-design';
import { useParams } from 'react-router-dom';
import { track, ClickEventName } from '@/track';

interface StoryEditCardProps {
  /** 카드 제목 */
  title: string;
  /** 카드 타입 */
  type: 'introduction' | 'development' | 'conclusion';
  /** 초기 내용 */
  content: string;
  /** 수정하기 버튼 클릭 핸들러 */
  onEdit?: () => void;
  /** 저장하기 버튼 클릭 핸들러 */
  onSave?: () => void;
  /** 내용 변경 핸들러 (실시간 데이터 동기화용) */
  onContentChange?: (content: string) => void;
  /** 읽기 전용 모드 */
  readOnly?: boolean;
  /** 최대 글자수 제한 */
  maxLength?: number;
  /** 편집 모드 변경 핸들러 */
  onEditModeChange?: (isEditing: boolean) => void;
}

function StoryEditCard({
  title,
  type,
  content,
  onEdit,
  onSave,
  onContentChange,
  readOnly = false,
  maxLength = 500,
  onEditModeChange
}: StoryEditCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentContent, setCurrentContent] = useState(content);
  const [currentLength, setCurrentLength] = useState(content.length);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { appId } = useParams<AppParam>();

  // textarea 높이 자동 조정 함수 (먼저 선언)
  const adjustTextareaHeight = useCallback((element: HTMLTextAreaElement) => {
    const textareaElement = element;
    textareaElement.style.height = 'auto';
    textareaElement.style.height = `${textareaElement.scrollHeight}px`;
  }, []);

  // content prop이 변경될 때 currentContent와 currentLength도 업데이트
  useEffect(() => {
    if (!isEditing) {
      setCurrentContent(content);
      setCurrentLength(content.length);
    }
  }, [content, isEditing]);

  // 편집 모드로 전환 시 textarea 높이 조정 (깜빡임 방지)
  useLayoutEffect(() => {
    if (isEditing && textareaRef.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [isEditing, adjustTextareaHeight]);

  // 편집 상태 변경 시 부모 컴포넌트에 알림
  useEffect(() => {
    onEditModeChange?.(isEditing);
  }, [isEditing, onEditModeChange]);

  // 내용 변경 시 textarea 높이 자동 조정
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      if (value.length <= maxLength) {
        setCurrentContent(value);
        setCurrentLength(value.length);
        onContentChange?.(value);
        // 부드러운 높이 자동 조정
        requestAnimationFrame(() => {
          adjustTextareaHeight(e.target);
        });
      }
    },
    [onContentChange, maxLength, adjustTextareaHeight]
  );

  // 수정 모드로 전환
  const handleEditClick = useCallback(() => {
    if (readOnly) return;
    track.onClick({
      clickEventName: ClickEventName.click_newgame_ai_storyconfirm_edit,
      params: {
        channel_id: appId
      }
    });
    setIsEditing(true);
    onEdit?.();
  }, [onEdit, readOnly]);

  // 저장하고 뷰 모드로 전환
  const handleSaveClick = useCallback(() => {
    setIsEditing(false);
    onSave?.();
  }, [onSave]);

  // 저장 버튼 비활성화 여부 (편집 모드이고 내용이 비어있는 경우)
  const isSaveDisabled = isEditing && currentContent.trim() === '';

  return (
    <div css={CardContainer}>
      <div css={CardContent}>
        {/* 헤더 영역 */}
        <div css={Header}>
          <h3 css={Title}>{title}</h3>
          {!readOnly && (
            <Button
              size="medium"
              type={isEditing ? 'primary' : 'basic'}
              onClick={isEditing ? handleSaveClick : handleEditClick}
              disabled={isSaveDisabled}
            >
              {isEditing ? '저장하기' : '수정하기'}
            </Button>
          )}
        </div>

        {/* 내용 영역 */}
        <div css={ContentArea}>
          {isEditing ? (
            <div css={EditContainer}>
              <textarea
                ref={textareaRef}
                css={TextArea}
                value={currentContent}
                onChange={handleTextareaChange}
                placeholder="내용을 입력하세요"
              />
              <span css={CharCount}>
                {currentLength}/{maxLength}
              </span>
            </div>
          ) : (
            <div css={ContentTextContainer}>
              <div css={ContentText}>
                {currentContent.split('\n').map((line) => (
                  <p key={line} css={Paragraph}>
                    {line}
                  </p>
                ))}
              </div>
              <span css={CharCount}>
                {currentLength}/{maxLength}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoryEditCard;

const CardContainer = css`
  background-color: ${color.surface_default_default};
  border-radius: 16px;
  border: 1px solid ${color.border_default_subdued};
  overflow: hidden;
  position: relative;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
`;

const CardContent = css`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${color.text_default};
  margin: 0;
`;

const ContentArea = css`
  background-color: ${color.surface_default_subdued};
  border-radius: 8px;
  padding: 12px;
  min-height: 60px; /* 최소 높이 줄임 - 내용에 따라 유연하게 조정 */
  height: auto; /* 내용에 따라 높이 자동 조절 */
  max-height: none; /* 최대 높이 제한 완전 제거 */
  overflow: visible; /* 모든 방향으로 확장 허용 */
  overflow-wrap: break-word; /* 긴 단어 줄바꿈 */
  word-break: break-word; /* 긴 단어 강제 줄바꿈 */
  transition: height 0.2s ease-out; /* 부드러운 높이 전환 */
`;

const ContentTextContainer = css`
  position: relative;
`;

const ContentText = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${color.text_subdued};
  word-wrap: break-word; /* 긴 단어 자동 줄바꿈 */
  overflow-wrap: break-word; /* CSS 표준 속성 */
  word-break: break-word; /* 긴 단어 강제 줄바꿈 */
  hyphens: auto; /* 하이픈 자동 적용 */
  white-space: pre-wrap; /* 줄바꿈과 공백 보존 */
  overflow-x: hidden; /* 가로 스크롤 방지 */
  padding: 0 0 28px 0; /* 글자수 표시 줄의 높이만큼 하단 패딩 */
  min-height: auto; /* 내용에 따라 높이 자동 조정 */
`;

const Paragraph = css`
  margin: 0 0 8px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EditContainer = css`
  position: relative;
`;

const TextArea = css`
  width: 100%;
  min-height: 40px; /* 최소 높이 줄임 - 내용에 따라 유연하게 조정 */
  border: none;
  background-color: transparent;
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${color.text_subdued};
  resize: none; /* 리사이즈 핸들 제거 */
  outline: none;
  overflow: hidden; /* JavaScript로 높이 제어 */
  word-wrap: break-word; /* 긴 단어 자동 줄바꿈 */
  overflow-wrap: break-word; /* CSS 표준 속성 */
  word-break: break-word; /* 긴 단어 강제 줄바꿈 */
  hyphens: auto; /* 하이픈 자동 적용 */
  padding: 0 0 32px 0; /* 글자수 표시 줄의 높이만큼 하단 패딩 */
  transition: height 0.2s ease-out; /* 부드러운 높이 전환 */

  &::placeholder {
    color: ${color.text_subdued};
    opacity: 0.6;
  }

  &:focus {
    outline: none;
  }
`;

const CharCount = css`
  position: absolute;
  right: 0;
  bottom: 0;
  font-family: 'Pretendard', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #626262;
  pointer-events: none;
  background-color: ${color.surface_default_subdued};
  padding: 4px 8px;
  border-radius: 4px;
`;
